import type { ApiClient } from '@carbonwebmail/api-client'
import { useCallback, useState } from 'react'

/**
 * The unsubscribe request, kept out of the component.
 *
 * Logic in hooks, JSX in components: a view that fetches is a view that cannot
 * be tested without a network, and it is the shape that let the predecessor's
 * screens grow until each one was a small application.
 *
 * The client is passed in rather than imported, so the same hook runs against a
 * real origin in the browser and against an in-process API in a test.
 */

type UnsubscribeState =
  /** Waiting for the person to confirm. Nothing has been sent. */
  | { readonly status: 'idle' }
  | { readonly status: 'working' }
  | { readonly status: 'done' }
  | { readonly status: 'failed'; readonly detail: string }

interface UseUnsubscribeOptions {
  readonly token: string
  readonly client: ApiClient
}

export function useUnsubscribe({ token, client }: UseUnsubscribeOptions) {
  const [state, setState] = useState<UnsubscribeState>({ status: 'idle' })

  const confirm = useCallback(async () => {
    setState({ status: 'working' })
    try {
      const { recorded } = await client.unsubscribe(token)
      setState(
        recorded
          ? { status: 'done' }
          : {
              status: 'failed',
              detail: 'We could not record that just now. Please try again in a moment.',
            },
      )
    } catch {
      // A transport failure means we genuinely do not know whether it was
      // recorded, so the honest answer is to ask them to try again rather than
      // to show a confirmation we cannot stand behind.
      setState({
        status: 'failed',
        detail: 'We could not reach the server. Please check your connection and try again.',
      })
    }
  }, [client, token])

  return { state, confirm }
}
